import { computed, reactive, ref } from 'vue'
import {
    catalogueWidgets,
    chargerDisposition,
    reinitialiserDisposition,
    donneesWidget,
    enregistrerDisposition,
} from '@/api/dashboard'
import { composantDe, demandeSesDonnees } from '@/views/dashboard/widgets/registre'

/**
 * L'état de l'accueil par widgets : ce que cette personne peut voir, comment
 * c'est disposé, et où en est le chargement de chacun.
 *
 * Deux dispositions, pas une. `grille` est ce qui s'affiche — la bibliothèque
 * la réécrit elle-même sous 1200 px pour empiler les widgets sur moins de
 * colonnes, et cette version recalculée ne doit jamais partir au serveur.
 * `dispositionBureau` garde la seule qui compte : celle réglée sur écran large,
 * sur les douze colonnes que le serveur valide. Confondre les deux, c'est
 * enregistrer une pile d'une colonne le jour où quelqu'un ouvre l'accueil sur
 * son téléphone, et la retrouver sur son écran le lendemain.
 */

/** Le palier où la grille tient ses douze colonnes. Ailleurs, elle est en lecture. */
export const PALIER_BUREAU = 'lg'

/** Les douze colonnes du palier bureau, celles que le serveur valide. */
const COLONNES = 12

export function useDashboardAccueil() {
    const chargement = ref(true)
    /** Le serveur réserve l'accueil au périmètre interne : 403 pour un client extranet. */
    const interdit = ref(false)
    const panne = ref(false)

    const catalogue = ref([])
    const grille = ref([])
    const dispositionBureau = ref([])
    const source = ref('defaut')
    /** Widgets perdus faute de droit, comptés par le serveur. Un entier, sans leurs noms. */
    const retires = ref(0)
    /** Clés apparues depuis le dernier réglage : de quoi attirer l'œil une fois. */
    const aSignaler = ref([])
    /**
     * Le serveur a refusé une clé à l'enregistrement, et l'écran s'est réaligné
     * sur lui. Signe qu'un droit a changé pendant que l'accueil était ouvert.
     */
    const aligneSurLeServeur = ref(false)

    /** Par clé : { chargement, panne, donnees }. Un appel par widget, indépendants. */
    const etats = reactive({})

    const palier = ref(PALIER_BUREAU)
    const surBureau = computed(() => palier.value === PALIER_BUREAU)

    /**
     * Les widgets à poser, dans l'ordre de la disposition.
     *
     * `grille` ne contient déjà que des clés posables — voir `poser()`, qui
     * écarte les autres avant qu'elles n'entrent dans la grille. Le filtre est
     * gardé ici par prudence, il ne doit rien avoir à retirer.
     */
    const widgetsPoses = computed(() =>
        grille.value
            .map(place => ({
                place,
                fiche: ficheDe(place.i),
                composant: composantDe(place.i),
            }))
            .filter(({ fiche, composant }) => fiche !== null && composant !== null),
    )

    /**
     * Cette clé peut-elle être posée sur la grille ?
     *
     * Il faut une fiche au catalogue — le serveur retire déjà celles dont le
     * droit a disparu, mais une disposition enregistrée avant qu'un widget ne
     * soit supprimé du code y survivrait — et un composant pour la rendre.
     */
    function posable(cle) {
        return ficheDe(cle) !== null && composantDe(cle) !== null
    }

    /**
     * La hauteur sous laquelle ce widget coupe son contenu.
     *
     * C'est celle que le serveur annonce au catalogue : elle est mesurée sur le
     * rendu réel, widget par widget.
     */
    function hauteurMinimale(cle) {
        return ficheDe(cle)?.taille?.h ?? 0
    }

    /**
     * Ce que cette personne peut ajouter : autorisé par le serveur, rendu ici,
     * pas encore posé. Recalculé plutôt que reçu — le `nouveaux` du serveur dit
     * la même chose au chargement, mais cesse d'être vrai dès le premier ajout.
     */
    const aProposer = computed(() => {
        const poses = new Set(grille.value.map(place => place.i))

        return catalogue.value.filter(
            fiche => !poses.has(fiche.key) && composantDe(fiche.key) !== null,
        )
    })

    function ficheDe(cle) {
        return catalogue.value.find(fiche => fiche.key === cle) ?? null
    }

    async function initialiser() {
        chargement.value = true
        interdit.value = false
        panne.value = false

        // Les deux ensemble : le catalogue ne dépend pas de la disposition, et
        // l'accueil n'a rien à poser sans les deux.
        const [reponseCatalogue, reponseDisposition] = await Promise.allSettled([
            catalogueWidgets(),
            chargerDisposition(),
        ])

        if (refusee(reponseCatalogue) || refusee(reponseDisposition)) {
            interdit.value = true
            chargement.value = false
            return
        }

        if (reponseCatalogue.status === 'rejected' || reponseDisposition.status === 'rejected') {
            panne.value = true
            chargement.value = false
            return
        }

        // `utils/request` renvoie déjà le corps de la réponse : `reponse.data`
        // est donc l'enveloppe `data` de Laravel, pas la réponse HTTP. D'où un
        // seul niveau ici, et deux dans `charger()` où la charge utile du widget
        // est elle-même sous une clé `data`.
        catalogue.value = reponseCatalogue.value?.data ?? []

        const disposition = reponseDisposition.value?.data ?? {}

        source.value = disposition.source ?? 'defaut'
        retires.value = disposition.retires ?? 0
        aSignaler.value = disposition.nouveaux ?? []

        poser(disposition.layout ?? [])
        chargement.value = false

        // Un appel par widget, tous lancés ensemble : le plus lent ne retient
        // que sa propre carte.
        widgetsPoses.value.forEach(({ place }) => charger(place.i))
    }

    function refusee(reponse) {
        return reponse.status === 'rejected' && reponse.reason?.status === 403
    }

    /**
     * Installe une disposition venue du serveur.
     *
     * Aucun widget n'est ajouté ni décalé : la disposition envoyée au serveur
     * doit être exactement celle qu'il a servie, sinon les deux dérivent — et un
     * widget que le front réinsère de lui-même ne peut jamais être retiré,
     * puisque son absence n'est enregistrable nulle part.
     *
     * Les clés que cette version du front ne sait pas rendre sont en revanche
     * écartées, et pas seulement à l'affichage : la grille reçoit ce tableau
     * entier, compacte dessus et en tire sa hauteur. Une clé gardée sans
     * composant creusait une bande vide au milieu de l'accueil, dans laquelle
     * rien ne pouvait être glissé et qu'aucune croix ne permettait de retirer.
     * Conséquence assumée : le prochain enregistrement la purge.
     */
    function poser(layout) {
        const places = layout
            .filter(place => posable(place.key))
            .map(place => ({
                i: place.key,
                x: place.x,
                y: place.y,
                w: place.w,
                // Plancher de hauteur : les premières valeurs par défaut avaient
                // été fixées sans rendre l'écran, et coupaient le contenu. Les
                // corriger côté serveur ne suffit pas — une disposition déjà
                // enregistrée garde la sienne, et resterait tronquée. On la
                // relève ici à l'affichage, sans rien réécrire : le prochain
                // geste de l'utilisateur enregistrera la valeur corrigée.
                h: Math.max(place.h, hauteurMinimale(place.key)),
            }))

        dispositionBureau.value = copier(places)
        grille.value = copier(places)
    }

    /**
     * Rend à l'accueil le repli de son profil.
     *
     * Le serveur renvoie le repli qu'il vient de recalculer : on le pose tel
     * quel, sans plancher de hauteur à lui appliquer — ces hauteurs-là sortent
     * du registre à jour, ce sont elles le plancher. `source` repasse à
     * `defaut`, donc le prochain geste de l'utilisateur enregistrera de
     * nouveau.
     */
    async function reinitialiser() {
        // Dans la même file que les enregistrements, et pour la même raison
        // qu'eux. Un PUT parti juste avant — un widget décoché, le réseau qui
        // traîne — arrivait après la suppression et recréait la ligne : la
        // base retrouvait l'ancienne disposition, l'écran affichait le repli,
        // et le message annonçait une remise à zéro qui n'avait pas tenu.
        file = file.then(oublier, oublier)

        return file
    }

    async function oublier() {
        const reponse = await reinitialiserDisposition()

        source.value = reponse?.data?.source ?? 'defaut'
        retires.value = 0
        aSignaler.value = []
        // Les trois disent le même écart entre la disposition enregistrée et
        // les droits. Il n'y a plus de disposition enregistrée.
        aligneSurLeServeur.value = false
        poser(reponse?.data?.layout ?? [])

        widgetsPoses.value.forEach(({ place }) => charger(place.i))

        return true
    }

    async function charger(cle) {
        if (!demandeSesDonnees(cle)) return

        etats[cle] = { chargement: true, panne: false, donnees: null }

        try {
            const reponse = await donneesWidget(cle)

            etats[cle] = { chargement: false, panne: false, donnees: reponse?.data?.data ?? {} }
        } catch {
            // Rien de ce qui était affiché ne vaut plus : un chiffre d'il y a
            // dix minutes présenté comme celui de maintenant est une
            // information fausse, pas une information dégradée.
            etats[cle] = { chargement: false, panne: true, donnees: null }
        }
    }

    /**
     * La grille a bougé sur un geste réel.
     *
     * Refuse hors du palier bureau, même si l'appelant a déjà posé cette garde :
     * la bibliothèque recalcule la disposition pour les petits écrans, et cette
     * version-là n'est pas un réglage mais un affichage. La garde vit ici parce
     * qu'un second appelant, un jour, ne saura pas qu'il devait la tenir.
     */
    function retenir(layout) {
        if (!surBureau.value) return Promise.resolve(false)

        dispositionBureau.value = copier(
            layout.map(place => ({ i: place.i, x: place.x, y: place.y, w: place.w, h: place.h })),
        )

        return persister()
    }

    /**
     * Les enregistrements se suivent, ils ne se croisent pas.
     *
     * Deux PUT partis coup sur coup peuvent revenir dans l'ordre inverse, et
     * c'est alors la disposition la plus ancienne qui reste en base. Chaque appel
     * attend donc la fin du précédent — et reçoit le résultat de SA propre
     * écriture, pas celui de l'écriture d'à côté.
     */
    let file = Promise.resolve(true)

    function persister() {
        file = file.then(envoyer, envoyer)

        return file
    }

    async function envoyer() {
        const layout = dispositionBureau.value.map(place => ({
            key: place.i,
            x: place.x,
            y: place.y,
            w: place.w,
            h: place.h,
        }))

        try {
            const reponse = await enregistrerDisposition(layout)

            source.value = reponse?.data?.source ?? 'utilisateur'

            // Le `retires` de l'écriture ne dit PAS la même chose que celui de
            // la lecture. À la lecture : « des widgets ont disparu de votre
            // accueil, un droit vous a été retiré » — ce que le bandeau annonce.
            // À l'écriture : « des clés que vous venez d'envoyer ont été
            // refusées », normalement zéro. Les ranger dans la même valeur
            // effaçait le bandeau au premier déplacement, avant qu'il n'ait été
            // lu, et il ne revenait jamais.
            const enregistre = reponse?.data?.layout

            // `> 0` volontairement : un retour vide alors qu'on a envoyé
            // quelque chose n'est pas un filtrage, c'est une anomalie de
            // contrat. S'aligner dessus viderait l'accueil de l'utilisateur —
            // on préfère un écran en avance d'un widget, que le prochain
            // chargement corrigera, à un écran effacé.
            if (
                Array.isArray(enregistre)
                && enregistre.length > 0
                && enregistre.length !== layout.length
            ) {
                // Le serveur a refusé des clés : un droit a changé pendant que
                // l'écran était ouvert. S'aligner sur lui, sinon chaque geste
                // suivant les renvoie et se les fait refuser indéfiniment, en
                // refaisant clignoter le bandeau à chaque fois.
                poser(enregistre)
                aligneSurLeServeur.value = true
            }

            return true
        } catch {
            return false
        }
    }

    /**
     * Pose un widget du catalogue sous les autres, et charge ses données.
     *
     * Renvoie `null` quand il n'y avait rien à faire — clé déjà posée, fiche
     * absente — et non `false` : un double clic sur la même offre n'est pas un
     * échec d'écriture, et l'annoncer comme tel affirmerait une panne qui n'a
     * pas eu lieu.
     */
    function ajouter(cle) {
        if (grille.value.some(place => place.i === cle)) return Promise.resolve(null)

        const fiche = ficheDe(cle)

        if (fiche === null) return Promise.resolve(null)

        const place = {
            i: cle,
            x: 0,
            y: basDeGrille(),
            w: Math.min(fiche.taille?.w ?? 4, COLONNES),
            h: fiche.taille?.h ?? 3,
        }

        grille.value = [...grille.value, place]
        dispositionBureau.value = [...dispositionBureau.value, { ...place }]

        charger(cle)

        return persister()
    }

    function retirer(cle) {
        grille.value = grille.value.filter(place => place.i !== cle)
        dispositionBureau.value = dispositionBureau.value.filter(place => place.i !== cle)
        delete etats[cle]

        return persister()
    }

    /**
     * Rétablit la disposition de bureau dans la grille.
     *
     * À appeler au retour sur écran large : la bibliothèque garde une copie du
     * palier bureau prise à la première sortie et ne la rafraîchit jamais, si
     * bien qu'un aller-retour vers un petit écran ramènerait une disposition
     * antérieure aux derniers déplacements.
     */
    function rendreLaDispositionDeBureau() {
        grille.value = copier(dispositionBureau.value)
    }

    /**
     * La première ligne libre sous tout ce qui est posé, **sur écran large**.
     *
     * Mesurée sur `dispositionBureau` et non sur `grille` : sous 1200 px la
     * grille est une pile d'une ou deux colonnes, bien plus haute, et un widget
     * ajouté depuis un téléphone se serait vu attribuer une place calculée sur
     * cette pile — donc fausse pour l'écran de bureau où elle sera enregistrée.
     */
    function basDeGrille() {
        return dispositionBureau.value.reduce((bas, place) => Math.max(bas, place.y + place.h), 0)
    }

    function copier(places) {
        return places.map(place => ({ ...place }))
    }

    return {
        chargement,
        interdit,
        panne,
        catalogue,
        grille,
        dispositionBureau,
        source,
        retires,
        aSignaler,
        aligneSurLeServeur,
        etats,
        palier,
        surBureau,
        widgetsPoses,
        aProposer,
        ficheDe,
        initialiser,
        reinitialiser,
        charger,
        retenir,
        ajouter,
        retirer,
        rendreLaDispositionDeBureau,
    }
}
